import { useEffect, useState } from "react";
import { Link } from "wouter";
import { api, img } from "../lib/api";

type Thumb = { id: string; title: string; version: number; color: string };
type Stats = {
  totals: { assets: number; published: number; drafts: number; archived: number; bytes: number; downloads30: number; pagesUsing: number; users: number };
  coverage: { alt: number; altAi: number; analysed: number; consentClear: number; license: number; total: number };
  uploads: { m: string; n: number }[];
  downloads: { m: string; n: number }[];
  top: (Thumb & { pages: number; downloads: number })[];
  unused: (Thumb & { created_at: string })[];
  unusedCount: number;
  tags: { name: string; n: number }[];
  photographers: { name: string; n: number }[];
  downloadKinds: { kind: string; n: number }[];
};

const MONTHS = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
const KIND: Record<string, string> = { original: "Original", social: "Sociala medier", pptx: "PowerPoint" };
const nf = new Intl.NumberFormat("sv-SE");

/** De senaste tolv månaderna, även de utan aktivitet – annars ljuger x-axeln om tiden. */
function last12(rows: { m: string; n: number }[]) {
  const map = new Map(rows.map((r) => [r.m, r.n]));
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return { key, label: MONTHS[d.getMonth()]!, year: d.getFullYear(), n: map.get(key) ?? 0 };
  });
}

/** En serie, en axel: staplar per månad med hovring per stapel och direktetikett på senaste månaden. */
function MonthBars({ title, rows, unit }: { title: string; rows: { m: string; n: number }[]; unit: string }) {
  const data = last12(rows);
  const max = Math.max(1, ...data.map((d) => d.n));
  const sum = data.reduce((s, d) => s + d.n, 0);
  const [hover, setHover] = useState<number | null>(null);
  const H = 140;
  return (
    <figure className="card p-4">
      <figcaption className="mb-3 flex items-baseline justify-between">
        <span className="font-semibold">{title}</span>
        <span className="text-[13px] text-soft">{nf.format(sum)} {unit} senaste 12 mån</span>
      </figcaption>
      <div className="relative" onMouseLeave={() => setHover(null)}>
        {/* Rutnät: bara baslinje och maxlinje, dämpade. */}
        <div className="absolute inset-x-0 top-0 border-t border-dashed border-line" />
        <span className="absolute -top-2 left-0 bg-card pr-1 text-[11px] tabular-nums text-muted">{nf.format(max)}</span>
        <div className="flex items-end gap-[2px]" style={{ height: H }} role="img" aria-label={`${title}: ${data.map((d) => `${d.label} ${d.n}`).join(", ")}`}>
          {data.map((d, i) => (
            <div key={d.key} className="relative flex h-full flex-1 items-end" onMouseEnter={() => setHover(i)}>
              <div className={`w-full rounded-t-[4px] transition-opacity ${hover !== null && hover !== i ? "opacity-40" : ""}`}
                style={{ height: d.n ? Math.max(3, (d.n / max) * H) : 0, background: "var(--color-ember)" }} />
              {hover === i && (
                <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-ink px-2 py-1 text-[12px] text-white shadow">
                  {d.label} {d.year}: <b>{nf.format(d.n)}</b> {unit}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="border-t border-soft/40" />
        <div className="mt-1 flex gap-[2px] text-center text-[11px] text-muted">
          {data.map((d, i) => <span key={d.key} className="flex-1">{i % 2 === (data.length - 1) % 2 ? d.label : ""}</span>)}
        </div>
      </div>
    </figure>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card p-4">
      <div className="text-[12px] text-muted">{label}</div>
      <div className="mt-0.5 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-[12px] text-soft">{sub}</div>}
    </div>
  );
}

/** Andel av bilderna som uppfyller något – stapel med text, aldrig bara färg. */
function Meter({ label, n, total, note }: { label: string; n: number; total: number; note?: string }) {
  const pct = total ? Math.round((n / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-[13px]">
        <span>{label}</span>
        <span className="tabular-nums text-soft">{pct} % <span className="text-muted">({nf.format(n)}/{nf.format(total)})</span></span>
      </div>
      <div className="mt-1 h-2 overflow-hidden rounded-full bg-mist">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "var(--color-ember)" }} />
      </div>
      {note && <p className="mt-0.5 text-[11.5px] text-muted">{note}</p>}
    </div>
  );
}

function ThumbRow({ items, meta }: { items: Thumb[]; meta: (t: any) => string }) {
  return (
    <div className="divide-y divide-line">
      {items.map((t) => (
        <Link key={t.id} href={`/bild/${t.id}`} className="flex items-center gap-3 py-2 hover:bg-mist/60">
          <img src={img(t, 320, "1:1")} alt="" className="h-10 w-10 rounded-md object-cover" style={{ background: t.color }} />
          <span className="min-w-0 flex-1 truncate font-medium">{t.title || "Namnlös"}</span>
          <span className="shrink-0 text-[12.5px] text-soft">{meta(t)}</span>
        </Link>
      ))}
    </div>
  );
}

export function StatsPage() {
  const [s, setS] = useState<Stats | null>(null);
  useEffect(() => { api<Stats>("stats").then(setS); }, []);
  if (!s) return null;
  const t = s.totals, c = s.coverage;
  const gb = t.bytes / 1e9;
  return (
    <div className="mx-auto max-w-5xl space-y-6 pt-8">
      <h1 className="text-2xl font-semibold tracking-tight">Statistik</h1>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile label="Bilder" value={nf.format(t.assets)} sub={`${t.published} publicerade · ${t.drafts} utkast`} />
        <Tile label="Nedladdningar, 30 dagar" value={nf.format(t.downloads30)} sub="original och export" />
        <Tile label="Webbsidor som visar bilder" value={nf.format(t.pagesUsing)} />
        <Tile label="Lagring" value={gb < 1 ? `${Math.round(t.bytes / 1e6)} MB` : `${gb.toFixed(1)} GB`} sub={`${t.users} användare`} />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <MonthBars title="Uppladdade bilder" rows={s.uploads} unit="bilder" />
        <MonthBars title="Nedladdningar och exporter" rows={s.downloads} unit="st" />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="card space-y-3 p-4">
          <div className="font-semibold">Hur komplett är bildbanken?</div>
          <Meter label="Granskad alt-text" n={c.alt} total={c.total} note={c.altAi ? `${c.altAi} till har AI-skriven alt-text som väntar på granskning` : undefined} />
          <Meter label="Samtycke bedömt" n={c.consentClear} total={c.total} />
          <Meter label="Licens angiven" n={c.license} total={c.total} />
          <Meter label="AI-analyserade" n={c.analysed} total={c.total} />
        </div>
        <div className="card p-4">
          <div className="mb-1 font-semibold">Mest använda</div>
          <p className="mb-1 text-[12px] text-muted">Antal webbsidor som visar bilden och nedladdningar senaste året.</p>
          {s.top.length ? <ThumbRow items={s.top} meta={(x) => `${x.pages} sidor · ${x.downloads} nedl.`} />
            : <p className="py-4 text-[13px] text-muted">Ingen bild har använts eller laddats ner ännu.</p>}
        </div>
      </div>

      <div className="card p-4">
        <div className="mb-1 flex items-baseline justify-between">
          <span className="font-semibold">Används aldrig</span>
          <span className="text-[13px] text-soft">{s.unusedCount} bilder</span>
        </div>
        <p className="mb-1 text-[12px] text-muted">Publicerade i minst 30 dagar utan en enda webbsida eller nedladdning – kandidater att lyfta fram eller arkivera.</p>
        {s.unused.length ? <ThumbRow items={s.unused} meta={(x) => `publicerad ${x.created_at.slice(0, 10)}`} />
          : <p className="py-4 text-[13px] text-muted">Inga – alla publicerade bilder används.</p>}
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <ListCard title="Vanligaste taggar" rows={s.tags.map((x) => [x.name, x.n, `/?tags=${encodeURIComponent(x.name)}`])} />
        <ListCard title="Fotografer" rows={s.photographers.map((x) => [x.name, x.n, `/?photographer=${encodeURIComponent(x.name)}`])} />
        <ListCard title="Nedladdningar per typ, 12 mån" rows={s.downloadKinds.map((x) => [KIND[x.kind] ?? x.kind, x.n, null])} />
      </div>
      <p className="pb-6 text-[12px] text-muted">
        Visningar på webbplatsen räknas inte här – de flesta bilder levereras direkt ur Cloudflares cache och når aldrig servern.
        Trafiken finns i Cloudflare under Analytics.
      </p>
    </div>
  );
}

function ListCard({ title, rows }: { title: string; rows: [string, number, string | null][] }) {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <div className="card p-4">
      <div className="mb-2 font-semibold">{title}</div>
      {rows.length === 0 && <p className="text-[13px] text-muted">Inget ännu.</p>}
      <ul className="space-y-1.5">
        {rows.map(([name, n, href]) => {
          const label = <span className="truncate">{name}</span>;
          return (
            <li key={name} className="text-[13px]">
              <div className="flex justify-between gap-2">
                {href ? <Link href={href} className="truncate hover:text-ember">{name}</Link> : label}
                <span className="tabular-nums text-soft">{nf.format(n)}</span>
              </div>
              <div className="mt-0.5 h-1 rounded-full bg-mist"><div className="h-full rounded-full bg-ember/70" style={{ width: `${(n / max) * 100}%` }} /></div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
